'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Expand, X } from "lucide-react";
import Image from "next/image";
import { canOptimizeImageSource, getDisplayImageSource } from "@/lib/image-source";

interface GalleriaProps {
    photos: { image: string; defaultimage: string }[];
    slug?: string;
    articolo?: string | number;
}

const CarGallery: React.FC<GalleriaProps> = ({ photos, slug, articolo }) => {
    if (!photos || photos.length === 0) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 h-full flex items-center justify-center p-2">
                <Image
                    src="/autoinarrivo.webp"
                    alt="Foto non disponibile"
                    className="w-full h-full object-cover rounded-lg"
                    width={1200}
                    height={800}
                    sizes="(max-width: 1024px) 100vw, 75vw"
                />
            </div>
        );
    }

    const defaultIndex = useMemo(() => {
        const i = photos.findIndex(photo => photo.defaultimage === "1");
        return i !== -1 ? i : 0;
    }, [photos]);

    const resolvedPhotos = useMemo(
        () => photos.map((photo) => ({
            ...photo,
            image: getDisplayImageSource(photo.image, "/autoinarrivo.webp", { slug, articolo }),
        })),
        [articolo, photos, slug]
    );

    const [currentIndex, setCurrentIndex] = useState(defaultIndex);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loadedImages, setLoadedImages] = useState<Record<number, boolean>>({});

    const sidebarRef = useRef<HTMLDivElement>(null);
    const modalStripRef = useRef<HTMLDivElement>(null);
    const activeSidebarThumbRef = useRef<HTMLButtonElement>(null);
    const activeModalThumbRef = useRef<HTMLButtonElement>(null);

    const handleImageLoaded = useCallback((index: number) => {
        setLoadedImages(prev => prev[index] ? prev : { ...prev, [index]: true });
    }, []);

    const nextPhoto = () => setCurrentIndex(i => i === resolvedPhotos.length - 1 ? 0 : i + 1);
    const prevPhoto = () => setCurrentIndex(i => i === 0 ? resolvedPhotos.length - 1 : i - 1);

    // Auto-scroll sidebar to keep active thumb visible
    useEffect(() => {
        activeSidebarThumbRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [currentIndex]);

    // Auto-scroll modal strip to keep active thumb visible
    useEffect(() => {
        if (isModalOpen) {
            activeModalThumbRef.current?.scrollIntoView({ inline: 'nearest', behavior: 'smooth' });
        }
    }, [currentIndex, isModalOpen]);

    // Close modal on Escape
    useEffect(() => {
        if (!isModalOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsModalOpen(false);
            if (e.key === 'ArrowRight') nextPhoto();
            if (e.key === 'ArrowLeft') prevPhoto();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isModalOpen]);

    const currentPhoto = resolvedPhotos[currentIndex];
    const isCurrentImageLoaded = loadedImages[currentIndex];
    const currentPhotoIsOptimizable = canOptimizeImageSource(currentPhoto.image);

    return (
        <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 h-full p-2">
                <div className="flex flex-col lg:flex-row gap-3 h-full">

                    {/* Main image */}
                    <div className="relative flex-grow group">
                        <div
                            className="relative h-full cursor-zoom-in"
                            onClick={() => setIsModalOpen(true)}
                        >
                            <Image
                                src={currentPhoto.image}
                                alt={`Foto ${currentIndex + 1}`}
                                className="w-full h-full object-cover rounded-lg"
                                width={600}
                                height={400}
                                quality={72}
                                priority={currentIndex === defaultIndex}
                                placeholder="blur"
                                blurDataURL="/autoinarrivo.webp"
                                onLoad={() => handleImageLoaded(currentIndex)}
                                sizes="(max-width: 1024px) 100vw, 68vw"
                                unoptimized={!currentPhotoIsOptimizable}
                            />

                            {!isCurrentImageLoaded && (
                                <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-lg pointer-events-none">
                                    <div className="h-9 w-9 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
                                </div>
                            )}

                            <div className="absolute top-3 right-3 bg-black/40 backdrop-blur-sm text-white p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none max-lg:hidden">
                                <Expand size={14} />
                            </div>

                            <span className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/40 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full pointer-events-none">
                                {currentIndex + 1} / {resolvedPhotos.length}
                            </span>
                        </div>

                        <button
                            onClick={e => { e.stopPropagation(); prevPhoto(); }}
                            aria-label="Foto precedente"
                            className="absolute top-1/2 left-2 -translate-y-1/2 bg-white/90 backdrop-blur-sm text-gray-800 rounded-full p-1.5 shadow-sm hover:bg-white transition opacity-0 group-hover:opacity-100 max-lg:opacity-100"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button
                            onClick={e => { e.stopPropagation(); nextPhoto(); }}
                            aria-label="Foto successiva"
                            className="absolute top-1/2 right-2 -translate-y-1/2 bg-white/90 backdrop-blur-sm text-gray-800 rounded-full p-1.5 shadow-sm hover:bg-white transition opacity-0 group-hover:opacity-100 max-lg:opacity-100"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>

                    {/* Sidebar thumbnails — desktop, all photos, scrollable */}
                    <div
                        ref={sidebarRef}
                        className="flex flex-col gap-2 max-lg:hidden overflow-y-auto max-h-full pr-0.5"
                        style={{ scrollbarWidth: 'none' }}
                    >
                        {resolvedPhotos.map((photo, idx) => {
                            const isActive = idx === currentIndex;
                            return (
                                <button
                                    key={idx}
                                    ref={isActive ? activeSidebarThumbRef : null}
                                    onClick={() => setCurrentIndex(idx)}
                                    className="shrink-0 focus:outline-none"
                                    aria-label={`Vai alla foto ${idx + 1}`}
                                >
                                    <Image
                                        src={photo.image}
                                        alt={`Thumbnail ${idx + 1}`}
                                        className={`w-[108px] h-[72px] object-cover rounded-lg transition-all duration-150 shrink-0 ${
                                            isActive
                                                ? "ring-2 ring-primary ring-offset-1 opacity-100"
                                                : "opacity-50 hover:opacity-80"
                                        }`}
                                        width={108}
                                        height={72}
                                        quality={45}
                                        loading="lazy"
                                        sizes="108px"
                                        unoptimized={!canOptimizeImageSource(photo.image)}
                                    />
                                </button>
                            );
                        })}
                    </div>

                </div>
            </div>

            {/* Modal / Lightbox */}
            {isModalOpen && (
                <div
                    className="fixed inset-0 bg-black/90 backdrop-blur-md z-[9999] flex flex-col"
                    onClick={() => setIsModalOpen(false)}
                >
                    {/* Top bar */}
                    <div
                        className="flex items-center justify-between px-6 py-4 shrink-0"
                        onClick={e => e.stopPropagation()}
                    >
                        <span className="text-white/60 text-sm font-medium">
                            {currentIndex + 1} <span className="text-white/30">/ {resolvedPhotos.length}</span>
                        </span>
                        <button
                            onClick={() => setIsModalOpen(false)}
                            className="text-white/70 hover:text-white transition bg-white/10 hover:bg-white/20 p-2 rounded-full"
                            aria-label="Chiudi galleria"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Main image area */}
                    <div
                        className="relative flex-1 flex items-center justify-center px-14 min-h-0"
                        onClick={e => e.stopPropagation()}
                    >
                        <Image
                            src={currentPhoto.image}
                            alt={`Foto ${currentIndex + 1}`}
                            className="max-w-full max-h-full object-contain rounded-lg"
                            width={1200}
                            height={800}
                            quality={80}
                            loading="lazy"
                            onLoad={() => handleImageLoaded(currentIndex)}
                            sizes="100vw"
                            unoptimized={!currentPhotoIsOptimizable}
                            style={{ maxHeight: '100%', width: 'auto' }}
                        />

                        {/* Arrows overlaid */}
                        <button
                            onClick={prevPhoto}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-white bg-white/10 hover:bg-white/25 transition p-3 rounded-full"
                            aria-label="Foto precedente"
                        >
                            <ChevronLeft size={24} />
                        </button>
                        <button
                            onClick={nextPhoto}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white bg-white/10 hover:bg-white/25 transition p-3 rounded-full"
                            aria-label="Foto successiva"
                        >
                            <ChevronRight size={24} />
                        </button>
                    </div>

                    {/* Thumbnail strip */}
                    <div
                        ref={modalStripRef}
                        className="flex gap-2 px-6 py-4 overflow-x-auto shrink-0"
                        style={{ scrollbarWidth: 'none' }}
                        onClick={e => e.stopPropagation()}
                    >
                        {resolvedPhotos.map((photo, idx) => {
                            const isActive = idx === currentIndex;
                            return (
                                <button
                                    key={idx}
                                    ref={isActive ? activeModalThumbRef : null}
                                    onClick={() => setCurrentIndex(idx)}
                                    className="shrink-0 focus:outline-none"
                                    aria-label={`Vai alla foto ${idx + 1}`}
                                >
                                    <Image
                                        src={photo.image}
                                        alt={`Thumbnail ${idx + 1}`}
                                        className={`w-[80px] h-[54px] object-cover rounded-md transition-all duration-150 ${
                                            isActive
                                                ? "ring-2 ring-white opacity-100 scale-105"
                                                : "opacity-40 hover:opacity-70"
                                        }`}
                                        width={80}
                                        height={54}
                                        quality={40}
                                        loading="lazy"
                                        sizes="80px"
                                        unoptimized={!canOptimizeImageSource(photo.image)}
                                    />
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </>
    );
};

export default CarGallery;
